import { memo, useMemo } from 'react';
import { cn } from '@divlab/divanui';

import WikiArticle from '@Components/WikiArticle';
import Button from '@UI/Button';
import useNavigation from '@Navigation/useNavigation';
import useMedias from '@Hooks/useMedias';
import styles from './WikiArticlesCatalog.module.css';

import type { WikiArticleData } from '@Types/Wiki';
import type { UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query';
import type { FC, HTMLAttributes, MouseEvent } from 'react';

export interface WikiCategoryData {
  page: number;
  articles: WikiArticleData[];
  countLeft: number;
}

export interface WikiArticlesCatalogProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  category: UseInfiniteQueryResult<InfiniteData<WikiCategoryData>>;
  countRows: number;
  onMore?: (e: MouseEvent) => void;
}

const WikiArticlesCatalog: FC<WikiArticlesCatalogProps> = (props) => {
  const { className, category, countRows, onMore, ...restProps } = props;
  const { isMobile, isMobileM } = useMedias();
  const { pathname, search } = useNavigation();

  const linkToNextPage = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    const lastPage = category.data.pages[category.data.pages.length - 1];
    const pageNumber = lastPage.page + 1;

    if (pageNumber > 1) searchParams.set('page', pageNumber.toString());

    const qs = searchParams.toString();

    return `${pathname}${qs ? `?${qs}` : ''}`;
  }, [category.data, pathname, search]);

  const articles = useMemo(() => {
    const allArticles = [];
    category.data.pages.forEach((page) => {
      page.articles.forEach((article) => {
        allArticles.push(article);
      });
    });

    return allArticles;
  }, [category.data]);

  const countProductsInRow = useMemo(() => {
    if (isMobile) {
      return {
        '2-in-row': 1,
        '3-in-row': 1,
      };
    }

    if (isMobileM) {
      return {
        '2-in-row': 1,
        '3-in-row': 2,
      };
    }

    return {
      '2-in-row': 2,
      '3-in-row': 3,
    };
  }, [isMobile, isMobileM]);

  const slicedStructure = useMemo(() => {
    const newItems = [];

    for (let i = 0; i < countRows; i += 1) {
      newItems.push(i % 2 === 0 ? '2-in-row' : '3-in-row');
    }

    return newItems;
  }, [countRows]);

  const items = useMemo(() => {
    const result = [];
    let indexArticle = 0;
    slicedStructure.forEach((row) => {
      const countArticles = countProductsInRow[row];
      const offset = indexArticle + countArticles;
      const chunkArticles = articles.slice(indexArticle, offset);
      const elems = [];

      indexArticle += chunkArticles.length;

      chunkArticles.forEach((article) => {
        switch (row) {
          case '2-in-row':
            elems.push({
              double: true,
              content: <WikiArticle article={article} />,
            });
            break;

          case '3-in-row':
            elems.push({
              content: <WikiArticle article={article} />,
            });
            break;

          default:
        }
      });

      result.push(...elems);
    });

    return result;
  }, [countProductsInRow, articles, slicedStructure]);

  return (
    <div {...restProps} className={cn(styles.catalog, className)}>
      <div className={styles.list}>
        {items.map((item, index) => (
          <div
            className={cn(styles.item, {
              [styles.double]: item.double,
            })}
            key={index}
          >
            {item.content}
          </div>
        ))}
      </div>

      {category.hasNextPage && (
        <div className={styles.moreWrapper}>
          <Button
            preventDefault
            theme='dirty'
            to={linkToNextPage}
            className={styles.moreButton}
            waiting={category.isFetchingNextPage}
            onClick={onMore}
          >
            Смотреть еще
          </Button>
        </div>
      )}
    </div>
  );
};

export default memo(WikiArticlesCatalog);
